import { createContext, useContext, useState, useRef, useEffect, useCallback, type ReactNode } from "react";
import { normalizePracticeElapsedSeconds } from "@/lib/practiceSessionDisplay";

interface ItemTimerState {
  elapsed: number;
  isPaused: boolean;
  /** Wall-clock timestamp when this item last started/resumed (null if paused) */
  runStartedAt: number | null;
}

interface PersistedItemTimers {
  activeItemId: string | null;
  items: Record<string, { elapsedBase: number; isPaused: boolean; runStartedAt: number | null }>;
}

type SessionSource = "free" | "structured";

const LS_KEY = "drumflow-timer-state";
const LS_ITEM_KEY = "drumflow-item-timers";
interface PersistedTimerState {
  isActive: boolean;
  isPaused: boolean;
  sessionSource: SessionSource;
  sessionStartedAt: string | null;
  /** elapsed seconds accumulated before the current running stretch */
  elapsedBase: number;
  /** Date.now() when the timer last started / resumed (only meaningful when running) */
  runStartedAt: number | null;
}

function loadPersistedState(): PersistedTimerState | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PersistedTimerState;
  } catch { return null; }
}

function persistState(s: PersistedTimerState) {
  localStorage.setItem(LS_KEY, JSON.stringify(s));
}

function clearPersistedState() {
  localStorage.removeItem(LS_KEY);
}

function loadPersistedItemTimers(): PersistedItemTimers | null {
  try {
    const raw = localStorage.getItem(LS_ITEM_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PersistedItemTimers;
  } catch { return null; }
}

function persistItemTimers(activeItemId: string | null, timers: Record<string, ItemTimerState>) {
  const items: PersistedItemTimers["items"] = {};
  for (const [id, t] of Object.entries(timers)) {
    // Compute current elapsedBase for running timers
    let elapsedBase = t.elapsed;
    if (!t.isPaused && t.runStartedAt) {
      // elapsed already includes wall-clock delta from tick, but we store the base
      // We store the frozen elapsed as base and keep the runStartedAt
      elapsedBase = t.elapsed;
    }
    items[id] = { elapsedBase: t.elapsed, isPaused: t.isPaused, runStartedAt: t.runStartedAt };
  }
  localStorage.setItem(LS_ITEM_KEY, JSON.stringify({ activeItemId, items }));
}

function clearPersistedItemTimers() {
  localStorage.removeItem(LS_ITEM_KEY);
}
interface PracticeTimerContextValue {
  // Global session timer
  isActive: boolean;
  isPaused: boolean;
  elapsed: number;
  sessionSource: SessionSource;
  sessionStartedAt: string | null;
  
  start: (source?: SessionSource) => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;

  // Per-item timers (persist across pages)
  itemTimers: Record<string, ItemTimerState>;
  activeItemId: string | null;
  startItemTimer: (itemId: string) => void;
  pauseItemTimer: () => void;
  resumeItemTimer: () => void;
  stopItemTimer: (itemId: string) => void;
  getItemElapsed: (itemId: string) => number;
  resetItemTimers: () => void;
}

const PracticeTimerContext = createContext<PracticeTimerContextValue | null>(null); // v4

export function usePracticeTimer() {
  const ctx = useContext(PracticeTimerContext);
  if (!ctx) throw new Error("usePracticeTimer must be used within PracticeTimerProvider");
  return ctx;
}

export function PracticeTimerProvider({ children }: { children: ReactNode }) {

  // === Global session timer ===
  const [isActive, setIsActive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [sessionSource, setSessionSource] = useState<SessionSource>("free");
  const [sessionStartedAt, setSessionStartedAt] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const elapsedBaseRef = useRef(0);
  const runStartedAtRef = useRef<number | null>(null);
  const initialisedRef = useRef(false);

  // Restore from localStorage on mount
  useEffect(() => {
    if (initialisedRef.current) return;
    initialisedRef.current = true;
    const saved = loadPersistedState();
    if (!saved || !saved.isActive) return;

    let restoredElapsed = saved.elapsedBase;
    if (!saved.isPaused && saved.runStartedAt) {
      // Timer was running – add real-world time that passed
      restoredElapsed += Math.round((Date.now() - saved.runStartedAt) / 1000);
    }

    const normalizedElapsed = normalizePracticeElapsedSeconds(restoredElapsed);
    const exceededSafeDuration = restoredElapsed > normalizedElapsed;

    elapsedBaseRef.current = saved.isPaused || exceededSafeDuration ? normalizedElapsed : 0;
    runStartedAtRef.current = saved.isPaused || exceededSafeDuration ? null : Date.now();
    if (!saved.isPaused && !exceededSafeDuration) {
      // We set base to restoredElapsed and runStartedAt to now so the interval just adds 1/s
      elapsedBaseRef.current = normalizedElapsed;
      runStartedAtRef.current = Date.now();
    }

    setElapsed(normalizedElapsed);
    setSessionSource(saved.sessionSource);
    setSessionStartedAt(saved.sessionStartedAt ?? null);
    setIsPaused(saved.isPaused || exceededSafeDuration);
    setIsActive(true);
    if (exceededSafeDuration) {
      persistState({
        isActive: true,
        isPaused: true,
        sessionSource: saved.sessionSource,
        sessionStartedAt: saved.sessionStartedAt ?? null,
        elapsedBase: normalizedElapsed,
        runStartedAt: null,
      });
    }
  }, []);

  // Tick interval + persist every second
  useEffect(() => {
    if (isActive && !isPaused) {
      if (!runStartedAtRef.current) {
        runStartedAtRef.current = Date.now();
      }
      intervalRef.current = setInterval(() => {
        const now = Date.now();
        const newElapsed = normalizePracticeElapsedSeconds(
          elapsedBaseRef.current + Math.round((now - (runStartedAtRef.current ?? now)) / 1000),
        );
        setElapsed(newElapsed);
        persistState({
          isActive: true, isPaused: false, sessionSource,
          sessionStartedAt,
          elapsedBase: elapsedBaseRef.current,
          runStartedAt: runStartedAtRef.current,
        });
      }, 1000);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [isActive, isPaused, sessionSource, sessionStartedAt]);

  const start = useCallback((source: SessionSource = "free") => {
    const startedAt = new Date().toISOString();
    elapsedBaseRef.current = 0;
    runStartedAtRef.current = Date.now();
    setElapsed(0);
    setIsPaused(false);
    setSessionSource(source);
    setSessionStartedAt(startedAt);
    setIsActive(true);
    persistState({ isActive: true, isPaused: false, sessionSource: source, sessionStartedAt: startedAt, elapsedBase: 0, runStartedAt: Date.now() });
  }, []);

  const pause = useCallback(() => {
    // Freeze elapsed
    const now = Date.now();
    const frozen = normalizePracticeElapsedSeconds(
      elapsedBaseRef.current + Math.round((now - (runStartedAtRef.current ?? now)) / 1000),
    );
    elapsedBaseRef.current = frozen;
    runStartedAtRef.current = null;
    setElapsed(frozen);
    setIsPaused(true);
    persistState({ isActive: true, isPaused: true, sessionSource, sessionStartedAt, elapsedBase: frozen, runStartedAt: null });
  }, [sessionSource, sessionStartedAt]);

  const resume = useCallback(() => {
    runStartedAtRef.current = Date.now();
    setIsPaused(false);
    persistState({ isActive: true, isPaused: false, sessionSource, sessionStartedAt, elapsedBase: elapsedBaseRef.current, runStartedAt: Date.now() });
  }, [sessionSource, sessionStartedAt]);

  const stop = useCallback(() => {
    setIsActive(false);
    setIsPaused(false);
    setElapsed(0);
    setSessionSource("free");
    setSessionStartedAt(null);
    elapsedBaseRef.current = 0;
    runStartedAtRef.current = null;
    clearPersistedState();
  }, []);

  // === Per-item timers ===
  const [itemTimers, setItemTimers] = useState<Record<string, ItemTimerState>>(() => {
    const saved = loadPersistedItemTimers();
    if (!saved) return {};
    const restored: Record<string, ItemTimerState> = {};
    for (const [id, s] of Object.entries(saved.items)) {
      let elapsed = s.elapsedBase;
      if (!s.isPaused && s.runStartedAt) {
        elapsed += Math.round((Date.now() - s.runStartedAt) / 1000);
      }
      const normalizedElapsed = normalizePracticeElapsedSeconds(elapsed);
      restored[id] = {
        elapsed: normalizedElapsed,
        isPaused: s.isPaused || elapsed > normalizedElapsed,
        runStartedAt: s.isPaused || elapsed > normalizedElapsed ? null : (s.runStartedAt ?? Date.now()),
      };
    }
    return restored;
  });
  const [activeItemId, setActiveItemId] = useState<string | null>(() => {
    return loadPersistedItemTimers()?.activeItemId ?? null;
  });
  const itemIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Persist item timers whenever they change
  useEffect(() => {
    persistItemTimers(activeItemId, itemTimers);
  }, [activeItemId, itemTimers]);

  // Tick the active item timer using wall-clock
  useEffect(() => {
    if (activeItemId && !itemTimers[activeItemId]?.isPaused) {
      itemIntervalRef.current = setInterval(() => {
        setItemTimers(prev => {
          const item = prev[activeItemId];
          if (!item || !item.runStartedAt) return prev;
          const newElapsed = normalizePracticeElapsedSeconds(item.elapsed + 1);
          return { ...prev, [activeItemId]: { ...item, elapsed: newElapsed } };
        });
      }, 1000);
    } else if (itemIntervalRef.current) {
      clearInterval(itemIntervalRef.current);
      itemIntervalRef.current = null;
    }
    return () => { if (itemIntervalRef.current) clearInterval(itemIntervalRef.current); };
  }, [activeItemId, itemTimers[activeItemId]?.isPaused]);

  const startItemTimer = useCallback((itemId: string) => {
    if (!isActive) {
      const startedAt = new Date().toISOString();
      elapsedBaseRef.current = 0;
      runStartedAtRef.current = Date.now();
      setElapsed(0);
      setIsPaused(false);
      setSessionSource("structured");
      setSessionStartedAt(startedAt);
      setIsActive(true);
      persistState({ isActive: true, isPaused: false, sessionSource: "structured", sessionStartedAt: startedAt, elapsedBase: 0, runStartedAt: Date.now() });
    } else if (isPaused) {
      resume();
    }

    setItemTimers(prev => ({
      ...prev,
      [itemId]: { elapsed: prev[itemId]?.elapsed ?? 0, isPaused: false, runStartedAt: Date.now() },
    }));
    setActiveItemId(itemId);
  }, [isActive, isPaused, resume]);

  const pauseItemTimer = useCallback(() => {
    if (activeItemId) {
      setItemTimers(prev => ({
        ...prev,
        [activeItemId]: { ...prev[activeItemId], isPaused: true, runStartedAt: null },
      }));
    }
  }, [activeItemId]);

  const resumeItemTimer = useCallback(() => {
    if (activeItemId) {
      setItemTimers(prev => ({
        ...prev,
        [activeItemId]: { ...prev[activeItemId], isPaused: false, runStartedAt: Date.now() },
      }));
      if (isPaused) {
        resume();
      }
    }
  }, [activeItemId, isPaused, resume]);

  const stopItemTimer = useCallback((itemId: string) => {
    if (activeItemId === itemId) {
      setActiveItemId(null);
    }
  }, [activeItemId]);

  const getItemElapsed = useCallback((itemId: string) => {
    return itemTimers[itemId]?.elapsed ?? 0;
  }, [itemTimers]);

  const resetItemTimers = useCallback(() => {
    setActiveItemId(null);
    setItemTimers({});
    clearPersistedItemTimers();
  }, []);

  return (
    <PracticeTimerContext.Provider value={{
      isActive, isPaused, elapsed, sessionSource, sessionStartedAt, start, pause, resume, stop,
      itemTimers, activeItemId, startItemTimer, pauseItemTimer, resumeItemTimer,
      stopItemTimer, getItemElapsed, resetItemTimers,
    }}>
      {children}
    </PracticeTimerContext.Provider>
  );
}
